/**
 * Карточка поля v3 — вкладка «Обзор», TanStack Query хуки виджетов (PR-B).
 *
 * Читают существующие backend-эндпоинты (ничего нового на бэке):
 *   - тех. операции   → GET /api/v1/fields/{id}/operations   (operations.py)
 *   - контур поля     → GET /api/v1/fields/{id}/contours?include_geojson=true
 *   - NDVI-снимки     → GET /api/v1/ndvi/perfield-png/{id}/latest?n=3  (ndvi.py)
 *
 * Осмотры переиспользуют ключ ['field-inspections', fieldId] из
 * FieldInspectionsTab — общий кэш, один сетевой запрос на карточку.
 * Рельеф — общий хук useFieldTerrain (тот же ключ, что подэкран «Рельеф»).
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Inspection } from '../../inspections/inspectionLogic';
import type { OperationLike } from './widgetLogic';

/** GET /fields/{id}/operations — операции поля (новые сверху, backend ORDER BY). */
export function useFieldOperations(
  fieldId: string,
): UseQueryResult<OperationLike[], Error> {
  return useQuery<OperationLike[], Error>({
    queryKey: ['field-operations', fieldId],
    queryFn: () => api.get<OperationLike[]>(`/api/v1/fields/${fieldId}/operations`),
    staleTime: 30_000,
  });
}

/** GET /inspections?field_id=... — осмотры поля (общий ключ с вкладкой «Осмотры»). */
export function useFieldInspections(
  fieldId: string,
): UseQueryResult<Inspection[], Error> {
  return useQuery<Inspection[], Error>({
    queryKey: ['field-inspections', fieldId],
    queryFn: () =>
      api.get<Inspection[]>(`/api/v1/inspections?field_id=${fieldId}&limit=200`),
    staleTime: 30_000,
  });
}

export interface ContourGeo {
  id: string;
  contour_type: string;
  geojson?: { type?: string; coordinates?: unknown } | null;
  /** AK-447: с какой даты действует эта версия контура (ISO / 'legacy'). */
  valid_from?: string | null;
}

/** GET /fields/{id}/contours?include_geojson=true — контуры поля для мини-карты. */
export function useFieldContourGeo(
  fieldId: string,
): UseQueryResult<ContourGeo[], Error> {
  return useQuery<ContourGeo[], Error>({
    queryKey: ['field-contour-geo', fieldId],
    queryFn: () =>
      api.get<ContourGeo[]>(
        `/api/v1/fields/${fieldId}/contours?include_geojson=true`,
      ),
    staleTime: 5 * 60_000,
  });
}

export interface NdviSnapshot {
  date: string;
  png_url: string;
}

export interface NdviLatestResponse {
  field_id: string;
  count: number;
  requested: number;
  dates: NdviSnapshot[];
}

/** GET /ndvi/perfield-png/{id}/latest?n=N — последние scene-complete NDVI-снимки. */
export function useNdviLatest(
  fieldId: string,
  n = 3,
): UseQueryResult<NdviLatestResponse, Error> {
  return useQuery<NdviLatestResponse, Error>({
    queryKey: ['field-ndvi-latest', fieldId, n],
    queryFn: () =>
      api.get<NdviLatestResponse>(
        `/api/v1/ndvi/perfield-png/${fieldId}/latest?n=${n}`,
      ),
    staleTime: 5 * 60_000,
    // Снимков может не быть (нет покрытия) — это не ошибка, а пустой список;
    // ретраить бессмысленно.
    retry: 1,
  });
}
